import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { dashboardApi } from '../lib/api';
import { pollJob } from '../lib/pollJob';
import {
  Search, Zap, Loader2, Check, ArrowRight, X, ChevronDown
} from 'lucide-react';

// Pipeline categories (six white-label defaults) — drives the quick-add stream selector.
const BD_STREAMS = [
  'Clients',
  'Prospective Clients',
  'Investors',
  'Grants & Funding',
  'Partnerships',
  'Other / Unclassified',
];

const STEPS = [
  { label: 'Creating pipeline record', key: 'pipeline' },
  { label: 'Running research', key: 'research' },
  { label: 'Drafting outreach', key: 'outreach' },
];

const CommandPalette = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const [bdStream, setBdStream] = useState('Clients');
  const [showStreams, setShowStreams] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Cmd+K listener
  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (open) onClose();
        else {
          // App.js manages open state, so this triggers via parent
          // We rely on the parent's state for opening
        }
      }
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery('');
      setJobId(null);
      setJobStatus(null);
    }
  }, [open]);

  // Poll job status
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    pollJob({
      fetchStatus: () => dashboardApi.quickAddStatus(jobId).then(r => r.data),
      isComplete: (d) => d.status === 'complete',
      isError: (d) => d.status === 'error' || d.status === 'expired',
      getError: (d) => d.error || 'Quick add failed',
      onProgress: (d) => { if (!cancelled) setJobStatus(d); },
      onComplete: (d) => { if (!cancelled) setJobStatus(d); },
      onError: (msg) => { if (!cancelled) setJobStatus({ status: 'error', error: msg }); },
      intervalMs: 2000,
    });
    return () => { cancelled = true; };
  }, [jobId]);

  const handleLaunch = async () => {
    if (!query.trim()) return;
    try {
      const res = await dashboardApi.quickAdd(query.trim(), bdStream);
      setJobId(res.data.job_id);
    } catch (err) {
      console.error('Quick add error:', err);
    }
  };

  const handleReset = () => {
    setQuery('');
    setJobId(null);
    setJobStatus(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const currentStep = jobStatus?.step || 0;
  const isComplete = jobStatus?.status === 'complete';
  const isError = jobStatus?.status === 'error';
  const isRunning = jobId && !isComplete && !isError;

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
        onClick={(e) => { if (e.target === e.currentTarget && !isRunning) onClose(); }}
      >
        <div className="absolute inset-0 bg-[#0A1628]/80 backdrop-blur-sm" />
        <motion.div
          initial={{ scale: 0.97, opacity: 0, y: -10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.97, opacity: 0, y: -10 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-xl bg-[#0E1C33] border border-[#1A2F4C] rounded-xl shadow-2xl overflow-hidden"
          data-testid="command-palette"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-[#1A2F4C]">
            <Search className="w-5 h-5 text-[#A3B5C7]" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !isRunning) handleLaunch(); }}
              placeholder="Type a company name to BREACH..."
              className="flex-1 bg-transparent text-white text-sm placeholder:text-[#A3B5C7]/50 focus:outline-none"
              disabled={!!jobId}
              data-testid="command-palette-input"
            />
            <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-[#A3B5C7]">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* BD Stream selector + hint */}
          {!jobId && (
            <div className="px-5 py-3 border-b border-[#1A2F4C] flex items-center justify-between">
              <div className="relative">
                <button
                  onClick={() => setShowStreams(!showStreams)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-xs text-[#A3B5C7] hover:text-white transition-colors"
                  data-testid="cmd-bd-stream-selector"
                >
                  {bdStream}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showStreams && (
                  <div className="absolute top-full left-0 mt-1 w-52 bg-[#0E1C33] border border-[#1A2F4C] rounded-lg shadow-xl z-10 py-1">
                    {BD_STREAMS.map((s) => (
                      <button
                        key={s}
                        onClick={() => { setBdStream(s); setShowStreams(false); }}
                        className={`w-full text-left px-3 py-2 text-xs transition-colors ${s === bdStream ? 'text-[#00D4C8] bg-[#00D4C8]/10' : 'text-[#A3B5C7] hover:bg-[#0A1628] hover:text-white'}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {query.trim() && (
                <p className="text-xs text-[#A3B5C7]/60">
                  Press <kbd className="px-1.5 py-0.5 rounded bg-[#1A2F4C] text-[#A3B5C7] text-[10px] font-mono">Enter</kbd> to launch BREACH on <span className="text-white">{query}</span>
                </p>
              )}
            </div>
          )}

          {/* Progress steps */}
          {jobId && (
            <div className="px-5 py-4 space-y-3">
              {STEPS.map((step, i) => {
                const stepNum = i + 1;
                const isDone = currentStep > stepNum || isComplete;
                const isActive = currentStep === stepNum && !isComplete;
                return (
                  <div key={step.key} className="flex items-center gap-3" data-testid={`cmd-step-${step.key}`}>
                    {isDone ? (
                      <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-green-400" />
                      </div>
                    ) : isActive ? (
                      <div className="w-6 h-6 rounded-full bg-[#00D4C8]/20 flex items-center justify-center">
                        <Loader2 className="w-3.5 h-3.5 text-[#00D4C8] animate-spin" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-[#1A2F4C] flex items-center justify-center">
                        <span className="text-[10px] text-[#A3B5C7]">{stepNum}</span>
                      </div>
                    )}
                    <span className={`text-sm ${isDone ? 'text-green-400' : isActive ? 'text-white' : 'text-[#A3B5C7]/50'}`}>
                      {step.label} {isDone && '✓'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Error */}
          {isError && (
            <div className="px-5 py-3 border-t border-[#1A2F4C]">
              <p className="text-red-400 text-sm">{jobStatus?.error || 'Something went wrong'}</p>
            </div>
          )}

          {/* Result */}
          {isComplete && (
            <div className="px-5 py-4 border-t border-[#1A2F4C] space-y-3">
              {jobStatus?.research?.summary && (
                <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                  <p className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1 font-medium">Research Summary</p>
                  <p className="text-sm text-[#D4E2F0] line-clamp-3">{jobStatus.research.summary}</p>
                </div>
              )}
              {jobStatus?.outreach && (
                <div className="p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                  <p className="text-xs uppercase tracking-wider text-[#00D4C8] mb-1 font-medium">Outreach Draft</p>
                  <p className="text-sm text-[#D4E2F0] line-clamp-4 whitespace-pre-line">{jobStatus.outreach}</p>
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => { onClose(); navigate(`/targets/${jobStatus.target_id}`); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#00D4C8]/10 text-[#00D4C8] text-xs hover:bg-[#00D4C8]/20 transition-colors"
                  data-testid="cmd-view-research"
                >
                  Research <ArrowRight className="w-3 h-3" />
                </button>
                <button
                  onClick={() => { onClose(); navigate('/pipeline'); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#00D4C8]/10 text-[#00D4C8] text-xs hover:bg-[#00D4C8]/20 transition-colors"
                >
                  Pipeline <ArrowRight className="w-3 h-3" />
                </button>
                <button
                  onClick={() => { onClose(); navigate('/outreach'); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#00D4C8]/10 text-[#00D4C8] text-xs hover:bg-[#00D4C8]/20 transition-colors"
                >
                  Outreach <ArrowRight className="w-3 h-3" />
                </button>
                <button
                  onClick={handleReset}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#1A2F4C] text-[#A3B5C7] text-xs hover:text-white hover:border-[#00D4C8]/30 transition-colors"
                  data-testid="cmd-breach-another"
                >
                  <Zap className="w-3 h-3" /> BREACH Another
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          {!jobId && (
            <div className="px-5 py-3 border-t border-[#1A2F4C] flex justify-end">
              <button
                onClick={handleLaunch}
                disabled={!query.trim()}
                className="btn-primary flex items-center gap-2 text-sm"
                data-testid="cmd-launch-btn"
              >
                <Zap className="w-4 h-4" /> Launch BREACH
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CommandPalette;
